import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Modal from "components/base/Modal";
import css from "./style.scss";
import ResettableAutocompleteDropdown from "components/SearchDropdown/ResettableAutocompleteDropdown";
import CheckboxItem from "components/Checkbox";
import { getCampaignsIntegration } from "data/user/actions";
import { selectCampaignsList, selectBatchDialerSource } from "data/user/selectors";
import { pushBatchDialerProperties, pushBatchDialerContacts, preflightBatchDialerContacts, missingPhoneProperties } from "data/property/actions";
import { selectSelection } from "data/property/selectors";
import { useDispatch, useSelector } from "react-redux";
import ToastNotification from "../../app/components/ToastNotification";
import MissingPhoneNumbers from "../MissingPhoneNumbers";
import PhoneNumberUnavailable from "../PhoneNumberUnavailabe";

const PushtoBatchDialer = ({ isOpen, onClose, contactIds, selectedPropertyIds, agGridRequest, contacts }) => {

    const dispatch = useDispatch();
    const batchSource = useSelector(selectBatchDialerSource);
    const selectedProperty = useSelector(selectSelection);
    const campaigns = useSelector(selectCampaignsList);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState("success");
    const [toastMessage, setToastMessage] = useState("");
    const [pendingPushPayload, setPendingPushPayload] = useState(null);

    const [phoneType, setPhoneType] = useState({
        mobile: false,
        landline: false,
    });
    const [dncStatus, setDncStatus] = useState({
        publicDnc: false,
        myDnc: false,
    });
    const [resetKey, setResetKey] = useState(0);

    const [isPreflightLoading, setIsPreflightLoading] = useState(false);
    const [showMissingPhoneModal, setShowMissingPhoneModal] = useState(false);
    const [pendingContactPayload, setPendingContactPayload] = useState(null);
    const [preflightResponse, setPreflightResponse] = useState(null);
    const [showPhoneUnavailableModal, setShowPhoneUnavailableModal] = useState(false);
    const [phoneUnavailableData, setPhoneUnavailableData] = useState(null);

    const maxItemCountToPush = 30000;

    useEffect(() => {
        if (isOpen) {
            dispatch(getCampaignsIntegration());
            // Reset to default values when modal opens
            setSelectedPhoneNumber({ id: "all", name: "All" });
            setPhoneType({ mobile: true, landline: true });
        }
    }, [isOpen, dispatch]);

    const handleClose = () => {
        resetFields();
        onClose();
    }

    const phoneNumberItems = [
        { id: "all", name: "All" },
        { id: 1, name: "1" },
        { id: 2, name: "2" },
        { id: 3, name: "3" },
        { id: 4, name: "4" },
        { id: 5, name: "5" },
        { id: 6, name: "6" },
        { id: 7, name: "7" },
        { id: 8, name: "8" },
        { id: 9, name: "9" },
        { id: 10, name: "10" },
        { id: 11, name: "11" },
        { id: 12, name: "12" }
    ];

    const isSaveDisabled = !selectedCampaign || (!phoneType.mobile && !phoneType.landline) || isPreflightLoading;

    const handleCampaignSelect = (item) => {
        setSelectedCampaign(item);
    };

    const resetFields = () => {
        setSelectedCampaign(null);
        setSelectedPhoneNumber(null);
        setPhoneType({ mobile: false, landline: false });
        setDncStatus({ publicDnc: false, myDnc: false });
        setResetKey(prev => prev + 1);
        setIsPreflightLoading(false);
        setShowMissingPhoneModal(false);
        setPendingContactPayload(null);
        setPreflightResponse(null);
        setShowPhoneUnavailableModal(false);
        setPhoneUnavailableData(null);
    };

    const showToastMessage = (type, message) => {
        setToastType(type);
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    const handleSave = () => {
        const source = batchSource;
        const phoneCount = selectedPhoneNumber?.id === "all"
            ? 12
            : Number(selectedPhoneNumber?.id || 0);

        const payload = {
            campaignId: selectedCampaign?.id,
            campaignName: selectedCampaign?.name,
            phoneTypeMobileIncluded: phoneType.mobile,
            phoneTypeLandLineIncluded: phoneType.landline,
            publicDncEnabled: dncStatus.publicDnc,
            myDncEnabled: dncStatus.myDnc,
            maxPhoneNumbersPerContact: phoneCount,
        };

        // Set search context based on source
        const pushedMoreItemsThanLimitErrorMessage = `The maximum send limit is ${Math.floor(maxItemCountToPush / 1000)}k items, so please adjust your selection to continue.`;
        if (source === "PROPERTY") {
            if (agGridRequest.selected > maxItemCountToPush) {
                showToastMessage("error", pushedMoreItemsThanLimitErrorMessage);
                resetFields();
                onClose();
                return;
            }

            payload.agGridRequest = agGridRequest;
        } else if (source === "CONTACT") {
            if (contacts.selected > maxItemCountToPush) {
                showToastMessage("error", pushedMoreItemsThanLimitErrorMessage);
                resetFields();
                onClose();
                return;
            }

            if (contacts.selectedIds != null && Array.isArray(contacts.selectedIds) && contacts.selectedIds.length > 0) {
                contacts.selection = contacts.selectedIds;
            }

            payload.contactSearchContext = contacts;
        }

        if (source === "PROPERTY") {
            setIsPreflightLoading(true);
            dispatch(missingPhoneProperties(
                payload,
                (response) => {

                    setIsPreflightLoading(false);

                    const data = response?.response || response;

                    const missingPhoneNumberNotSkipTraced = data?.contactsMissingPhoneNumberNotSkipTraced || [];
                    const missingPhoneNumberSkipTraced = data?.contactsMissingPhoneNumberSkipTraced || [];

                    if (missingPhoneNumberNotSkipTraced.length > 0 && missingPhoneNumberSkipTraced.length === 0) {

                        setPreflightResponse(data);
                        setPendingPushPayload(payload);
                        setShowMissingPhoneModal(true);

                    } else if (missingPhoneNumberSkipTraced.length > 0 && missingPhoneNumberNotSkipTraced.length === 0) {
                        setPhoneUnavailableData(data);
                        setPreflightResponse(data);
                        setShowPhoneUnavailableModal(true);

                    } else if (missingPhoneNumberNotSkipTraced.length > 0 && missingPhoneNumberSkipTraced.length > 0) {

                        setPreflightResponse(data);
                        setPendingPushPayload(payload);
                        setShowMissingPhoneModal(true);

                    } else {

                        dispatch(pushBatchDialerProperties(
                            payload,
                            () => {
                                showToastMessage("success", "Leads are being pushed to BatchDialer.");
                                resetFields();
                                onClose();
                            },
                            () => showToastMessage("error", "Something went wrong. Please try again.")
                        ));
                    }

                },
                () => {
                    setIsPreflightLoading(false);
                    showToastMessage("error", "Something went wrong. Please try again.");
                }
            ));
        } else if (source === "CONTACT") {
            setIsPreflightLoading(true);
            dispatch(preflightBatchDialerContacts(
                payload,
                (response) => {
                    setIsPreflightLoading(false);

                    const data = response?.response || response;

                    const missingPhoneNumberNotSkipTraced = data?.contactsMissingPhoneNumberNotSkipTraced || [];
                    const missingPhoneNumberSkipTraced = data?.contactsMissingPhoneNumberSkipTraced || [];
                    const countMissingPhoneNumber = data?.contactCountMissingPhoneNumber || 0;

                    if (missingPhoneNumberNotSkipTraced.length > 0 && missingPhoneNumberSkipTraced.length === 0) {
                        // only not skip traced → Missing Phone Numbers modal
                        setPreflightResponse(data);
                        setPendingContactPayload(payload);
                        setShowMissingPhoneModal(true);
                    } else if (missingPhoneNumberNotSkipTraced.length > 0 && missingPhoneNumberSkipTraced.length > 0) {
                        // mix of both → show Missing Phone Numbers modal (notSkipTraced takes priority)
                        setPreflightResponse(data);
                        setPendingContactPayload(payload);
                        setShowMissingPhoneModal(true);
                    } else if (missingPhoneNumberSkipTraced.length > 0 && missingPhoneNumberNotSkipTraced.length === 0) {
                        setPhoneUnavailableData(data);
                        setShowPhoneUnavailableModal(true);
                    } else {
                        // All contacts have phones → push directly
                        dispatch(pushBatchDialerContacts(
                            payload,
                            () => {
                                showToastMessage("success", "Leads are being pushed to BatchDialer.");
                                resetFields();
                                onClose();
                            },
                            () => showToastMessage("error", "Something went wrong. Please try again.")
                        ));
                    }
                },
                () => {
                    setIsPreflightLoading(false);
                    showToastMessage("error", "Something went wrong. Please try again.");
                }
            ));
        }
    };

     const handleContinueWithoutSkipTrace = () => {

        setShowMissingPhoneModal(false);

        if (batchSource === "PROPERTY") {

            dispatch(pushBatchDialerProperties(
                pendingPushPayload,
                () => {
                    showToastMessage("success", "Leads are being pushed to BatchDialer.");
                    resetFields();
                    onClose();
                },
                () => showToastMessage("error", "Something went wrong. Please try again.")
            ));

        } else {

            dispatch(pushBatchDialerContacts(
                pendingContactPayload,
                () => {
                    showToastMessage("success", "Leads are being pushed to BatchDialer.");
                    resetFields();
                    onClose();
                },
                () => {
                    setIsPreflightLoading(false);
                    showToastMessage("error", "Something went wrong. Please try again.");
                }
            ));

        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                isCloseButton={false}
                uniqId="push-to-BatchDialer"
                // width="auto"
                borderRadius="12px"
                modalClassName={css.pushtoBatchDialerModal}
                className={classNames(css.clsAccPageModals, css.clsBillingModal)}
            >
                <div className={css.modalContainer}>
                    <div className={css.headertxt}>
                        <span>
                            Push to BatchDialer
                        </span>
                    </div>

                    <div className={css.campaignWrapper} key={resetKey}>
                        <ResettableAutocompleteDropdown
                            label="Assign to Campaign"
                            placeholder="Select Campaign"
                            items={campaigns && campaigns.length > 0 ? campaigns : []}
                            onSelect={handleCampaignSelect}
                            required={true}
                            noDataMessage="No campaigns found"
                        />
                    </div>
                    <div className={css.checkboxGroupWrapper} key={`phoneType-${resetKey}`}>
                        <label className={css.checkboxGroupLabel}>Phone Type <span className={css.requiredAsterisk}>*</span></label>
                        <div className={css.checkboxGroup}>
                            <CheckboxItem
                                label="Mobile"
                                checked={phoneType.mobile}
                                onChange={() => setPhoneType(p => ({ ...p, mobile: !p.mobile }))}
                            />

                            <CheckboxItem
                                label="Landline"
                                checked={phoneType.landline}
                                onChange={() => setPhoneType(p => ({ ...p, landline: !p.landline }))}
                            />
                        </div>
                    </div>
                    <div className={css.phoneWrapper} key={`phone-${resetKey}`}>
                        <ResettableAutocompleteDropdown
                            label="Phone Numbers Per Record to Push"
                            placeholder="Select or Enter Number"
                            items={phoneNumberItems}
                            onSelect={(item) => setSelectedPhoneNumber(item)}
                            required={false}
                            value={selectedPhoneNumber}
                        />
                    </div>
                    <div className={css.checkboxGroupWrapper} key={`dnc-${resetKey}`}>
                        <label className={css.checkboxGroupLabel}>Include Phone Status</label>
                        <div className={css.checkboxGroup}>
                            <CheckboxItem
                                label="Public DNC"
                                checked={dncStatus.publicDnc}
                                onChange={() => setDncStatus(p => ({ ...p, publicDnc: !p.publicDnc }))}
                            />
                            <CheckboxItem
                                label="My DNC"
                                checked={dncStatus.myDnc}
                                onChange={() => setDncStatus(p => ({ ...p, myDnc: !p.myDnc }))}
                            />
                        </div>
                    </div>
                    <div className={css.buttonWrapper}>

                        <button className={css.cancelBtn} onClick={handleClose}>
                            <span className={css.cancelBtnLabel}>Cancel</span>
                        </button>

                        {/* Primary button */}
                        <button
                            className={classNames(css.connectModalBtn)}
                            disabled={isSaveDisabled}
                            onClick={handleSave}
                        >
                            <span className={css.connectBtnLabel}>
                                Save
                            </span>
                        </button>

                    </div>
                </div>
            </Modal>

            <MissingPhoneNumbers
                isOpen={showMissingPhoneModal}
                onClose={() => {
                    setShowMissingPhoneModal(false);
                }}
                campaignName={selectedCampaign?.name}
                missingCount={preflightResponse?.contactCountMissingPhoneNumber || 0}
                preflightResponse={preflightResponse}
                onContinueWithoutSkipTrace={handleContinueWithoutSkipTrace}
                showToastMessage={showToastMessage}
            />

            <PhoneNumberUnavailable
                isOpen={showPhoneUnavailableModal}
                onClose={() => {
                    setShowPhoneUnavailableModal(false);
                }}
                totalAttempted={phoneUnavailableData?.totalContactCount || 0}
                unavailableCount={phoneUnavailableData?.contactsMissingPhoneNumberSkipTraced?.length || 0}
                preflightResponse={phoneUnavailableData}
                showToastMessage={showToastMessage}
            />

            <ToastNotification message={toastMessage} show={showToast} type={toastType} />
        </>

    );
};

export default PushtoBatchDialer;
